/** Thin fetch wrapper for /api/v1. Cookies carry the session; errors become ApiError. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

/** UUID v4 that also works on plain-http LAN addresses, where crypto.randomUUID is unavailable. */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      /* insecure context */
    }
  }
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  /** Sends an Idempotency-Key. Pass a string to reuse a key across retries, or true for a fresh one. */
  idempotencyKey?: string | true;
}

export function qs(query?: Query) {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * The session lives only in the httpOnly cookies the server sets (z3s staff, z3c customer), which page scripts
 * cannot read. The web app never keeps a sign-in token in localStorage, where any injected script could steal it.
 * An earlier build did; this removes what it left behind (called on sign-in and sign-out).
 */
export function clearStoredToken() {
  try {
    localStorage.removeItem('z3_token');
  } catch {
    /* private mode */
  }
}

/**
 * After Sign out, pages that are still on screen for a moment would re-request their data without a
 * session and show 401s in the console. Until the next sign-in attempt, only /auth calls go out.
 */
let signedOut = false;
export function markSignedOut() {
  signedOut = true;
}
/** True between a sign-out and the next sign-in: nothing but /auth calls may go to the server. */
export const isSignedOut = () => signedOut;

export async function api<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (signedOut) {
    if (path === '/auth/login' || path.startsWith('/customer/auth/')) signedOut = false;
    else if (!path.startsWith('/auth/')) throw new ApiError(0, 'signed_out', 'You are signed out');
  }
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey === true ? uuid() : opts.idempotencyKey;
  const res = await fetch(`/api/v1${path}${qs(opts.query)}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const err = (data as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`, err?.details ?? data);
  }
  return data as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Builds a URL for file downloads (exports) that rely on the session cookie. */
export const downloadUrl = (path: string, query?: Query) => `/api/v1${path}${qs(query)}`;

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'bad_request' && Array.isArray(err.details) && err.details.length) {
      return (err.details as { path: string; message: string }[]).map((d) => (d.path ? `${d.path}: ${d.message}` : d.message)).join('; ');
    }
    return err.message;
  }
  if (err instanceof TypeError) return 'Network error. Check your connection and try again.';
  return err instanceof Error ? err.message : 'Something went wrong';
}
